export type BookDropImage = {
  src: string;
  alt: string;
};

export type BookDrop = {
  number: number;
  title: string;
  description?: string;
  additionalDescription?: string;
  images: BookDropImage[];
};

type BookDropContent = Omit<BookDrop, 'number'>;

type BookDropYearConfig = {
  introText?: string;
  showFullMap: boolean;
  fullMap?: BookDropImage;
  drops: BookDrop[];
};

const fountainOfYouth: BookDropContent = {
  title: 'The Fountain of Youth',
  description:
    "Easiest access is from Kummer Road, via the Victoria Secret trail. There's also a lightly used access trail off of Orange that can drop you down to it.",
  images: [
    { src: require('../assets/book-drops/fountain-of-youth-access.jpg'), alt: 'Fountain of Youth Access' },
    { src: require('../assets/book-drops/fountain-of-youth.jpg'), alt: 'Fountain of Youth' },
    { src: require('../assets/book-drops/fountain-of-youth-map.png'), alt: 'Fountain of Youth Map' },
  ],
};

const bathtub: BookDropContent = {
  title: 'The Bathtub',
  images: [
    { src: require('../assets/book-drops/bathtub-lee.jpg'), alt: 'The Bathtub' },
    { src: require('../assets/book-drops/bathtub-map.png'), alt: 'The Bathtub Map' },
  ],
};

const westernTerminus: BookDropContent = {
  title: 'The (almost) Western Terminus',
  description:
    "Follow Carson trail through McKinney park until the wooded section of trail ends by the goldfish swim school.",
  additionalDescription: '* Aid station 1, water and ice will be available',
  images: [
    { src: require('../assets/book-drops/western-terminus.jpg'), alt: 'The Western Terminus' },
    { src: require('../assets/book-drops/western-terminus-map.png'), alt: 'The Western Terminus Map' },
  ],
};

const northField: BookDropContent = {
  title: 'The North Field sign',
  description: 'Take the green trail all the way up the hill.',
  images: [
    { src: require('../assets/book-drops/north-field.jpg'), alt: 'North Field Sign' },
    { src: require('../assets/book-drops/north-field-map.png'), alt: 'North Field Map' },
  ],
};

const redYellowMerge: BookDropContent = {
  title: 'The Red/Yellow Merge',
  images: [
    { src: require('../assets/book-drops/red-yellow-merge.jpg'), alt: 'The Red/Yellow Merge' },
    { src: require('../assets/book-drops/red-yellow-merge-map.png'), alt: 'The Red/Yellow Merge Map' },
  ],
};

const spillway: BookDropContent = {
  title: 'The Spillway',
  description: 'Book will be on a rock in the spillway somewhere.',
  additionalDescription: '* Aid station 3 is the 7/11 after passing this drop.',
  images: [
    { src: require('../assets/book-drops/spillway.jpg'), alt: 'The Spillway' },
    { src: require('../assets/book-drops/spillway-map.png'), alt: 'The Spillway Map' },
  ],
};

const foreverTree: BookDropContent = {
  title: 'The Forever Tree',
  images: [
    { src: require('../assets/book-drops/forever-tree.jpg'), alt: 'The Forever Tree' },
    { src: require('../assets/book-drops/forever-tree-map.png'), alt: 'The Forever Tree Map' },
  ],
};

const abandonedPurple: BookDropContent = {
  title: 'The Abandoned Shelter on Purple',
  description: 'The book will be hidden in the rafters of the pavilion.',
  images: [
    { src: require('../assets/book-drops/abandoned-purple.jpg'), alt: 'The Abandoned Shelter on Purple' },
    { src: require('../assets/book-drops/abandoned-purple-map.png'), alt: 'The Abandoned Shelter on Purple Map' },
  ],
};

const natureBridge: BookDropContent = {
  title: 'The Nature Bridge',
  description:
    'Book will be under the bridge near the entrance to the Latodami Nature Center.',
  images: [
    { src: require('../assets/book-drops/nature-bridge.png'), alt: 'The Nature Bridge at Latodami Nature Center' },
    { src: require('../assets/book-drops/nature-bridge-boardwalk.png'), alt: 'The Nature Bridge boardwalk' },
    { src: require('../assets/book-drops/nature-bridge-map.png'), alt: 'The Nature Bridge Map' },
  ],
};

const oldWisconsin: BookDropContent = {
  title: 'Old Wisconsin',
  description:
    'Book will be in the rafters of the apparently abandoned and no longer named Wisconsin pavilion.',
  additionalDescription: '* Aid station 2, unless you run too fast and beat us there.',
  images: [
    { src: require('../assets/book-drops/old-wisconsin-sign.png'), alt: 'Old Wisconsin sign' },
    { src: require('../assets/book-drops/old-wisconsin-pavilion.png'), alt: 'Old Wisconsin pavilion' },
    { src: require('../assets/book-drops/old-wisconsin-map.png'), alt: 'Old Wisconsin Map' },
  ],
};


function numberedDrop(number: number, content: BookDropContent): BookDrop {
  return { number, ...content };
}

export const SUPPORTED_BOOK_DROP_YEARS = [2025, 2026] as const;
export type BookDropYear = (typeof SUPPORTED_BOOK_DROP_YEARS)[number];
export const DEFAULT_BOOK_DROP_YEAR: BookDropYear = 2026;

export const bookDropYears: Record<BookDropYear, BookDropYearConfig> = {
  2025: {
    showFullMap: true,
    fullMap: {
      src: require('../assets/book-drops/2025-full-map.png'),
      alt: '2025 Full Course Map',
    },
    drops: [
      numberedDrop(1, fountainOfYouth),
      numberedDrop(2, bathtub),
      numberedDrop(3, westernTerminus),
      numberedDrop(4, northField),
      numberedDrop(5, redYellowMerge),
      numberedDrop(6, spillway),
      numberedDrop(7, foreverTree),
      numberedDrop(8, abandonedPurple),
    ],
  },
  2026: {
    showFullMap: false,
    introText:
      'The course will follow a roughly clockwise route mostly around the perimeter of North Park. You will collect the books in order from 1-8 for the first 25k and return to the start/finish line to turn in your pages. 50k runners will then complete a second loop in reverse, collecting books from 8-1.',
    drops: [
      numberedDrop(1, fountainOfYouth),
      numberedDrop(2, {
        ...bathtub,
        description: 'Take either trail behind Pigeon Shelter to drop down to the bathtub.',
      }),
      numberedDrop(3, westernTerminus),
      numberedDrop(4, natureBridge),
      numberedDrop(5, oldWisconsin),
      numberedDrop(6, spillway),
      numberedDrop(7, foreverTree),
      numberedDrop(8, abandonedPurple),
    ],
  },
};

export function parseBookDropYear(value: string | null): BookDropYear {
  const year = Number(value);
  if (year === 2025 || year === 2026) {
    return year;
  }
  return DEFAULT_BOOK_DROP_YEAR;
}
